/**
 * Phone verification screen
 *
 * Route: `/webAppOnboarding/phone-verification`. Collects phone number and signs in via
 * `webAppOnboardingAuthentication` custom token.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` — same sign-up id / invite gate as welcome (no Firebase session yet).
 * - Auth: none on entry — outside `OnboardingAuthGuard`; signs in on submit. Already-authenticated users
 *   skip callable and use forward to continue.
 * - Onboarding state: none — outside `OnboardingCompletedGuard`. After auth, `navigateAfterAuth` sends
 *   `webAppOnboarding.state === 'completed'` patients to install, others to insurance-select.
 * - Screen-local: requires `signUpId`; waits for Firebase `onAuthStateChanged`; `waitForSignedUpPatientReady`
 *   before post-auth navigation. When the invitation is missing or does not load, the sign-up gate shows the error here.
 */

import { PATIENT_TRACKING_EVENTS, SignedUpPatient } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { FirebaseError } from 'firebase/app';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { auth } from '../../../firebaseConfig';
import { colors } from '../../../styles/colors';
import { space } from '../../../styles/onboardingUiTokens';
import { webAppOnboardingAuthentication } from '../api/webAppOnboardingAuthentication';
import { Button } from '../components/actions/Button';
import { Notice } from '../components/elements/Notice';
import { PhoneNumberInput } from '../components/forms/PhoneNumberInput';
import { buildOnboardingPersonProperties } from '../integrations/posthog/init';
import { SvgIcon } from '../components/icons/SvgIcon';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1 } from '../components/typography/HeadingLevels';
import { useOnboardingChatwoot } from '../integrations/chatwoot/OnboardingChatwootProvider';
import {
  isOnboardingCompletedRedirectEnforced,
  isWebAppOnboardingCompleted,
  persistOnboardingCurrentStep,
} from '../services/onboardingProgress';
import { waitForSignedUpPatientReady } from '../services/waitForSignedUpPatientReady';
import { getSignUpId, getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: false,
} as const;

const INSTALL_PATH = '/webAppOnboarding/install';

export function WebOnboardingPhoneVerificationScreen() {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { openSupportChat, isOpening: isOpeningSupportChat } = useOnboardingChatwoot();
  const signUpId = useOnboardingStore(getSignUpId);
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const setVerifiedPatientNamesFromAuth = useOnboardingStore((s) => s.setVerifiedPatientNamesFromAuth);

  const [authReady, setAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [countryCode, setCountryCodeState] = useState('+49');
  const [nationalNumber, setNationalNumberState] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const permissionDeniedStreakRef = useRef(0);
  const [permissionDeniedStreak, setPermissionDeniedStreak] = useState(0);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [postAuthNavigating, setPostAuthNavigating] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setAuthReady(true);
    });
    return unsubscribe;
  }, []);

  const confirmedPhone = useMemo(() => {
    if (!isAuthenticated) {
      return null;
    }
    if (!signedUpPatient) {
      return { countryCode: '+49', nationalNumber: '' };
    }
    return phonePartsFromSignedUpPatient(signedUpPatient);
  }, [isAuthenticated, signedUpPatient]);

  const resetPhoneErrors = useCallback(() => {
    setFieldError(undefined);
    permissionDeniedStreakRef.current = 0;
    setPermissionDeniedStreak(0);
  }, []);

  const setCountryCode = useCallback(
    (value: string) => {
      setCountryCodeState(value);
      resetPhoneErrors();
    },
    [resetPhoneErrors],
  );

  const setNationalNumber = useCallback(
    (value: string) => {
      setNationalNumberState(value);
      resetPhoneErrors();
    },
    [resetPhoneErrors],
  );

  const handleBack = useCallback(() => {
    navigate('..', { relative: 'path' });
  }, [navigate]);

  const handleOpenSupport = useCallback(() => {
    void openSupportChat();
  }, [openSupportChat]);

  const navigateAfterAuth = useCallback(async () => {
    if (!signUpId?.trim()) {
      return;
    }
    setPostAuthNavigating(true);
    try {
      const result = await waitForSignedUpPatientReady(signUpId);
      if (result.status !== 'ready') {
        return;
      }
      const { patient } = result;
      if (isOnboardingCompletedRedirectEnforced(patient.id) && isWebAppOnboardingCompleted(patient)) {
        persistOnboardingCurrentStep('install');
        navigate(INSTALL_PATH, { replace: true });
        return;
      }
      persistOnboardingCurrentStep('insurance_select');
      navigate('/webAppOnboarding/insurance-select');
    } finally {
      setPostAuthNavigating(false);
    }
  }, [navigate, signUpId]);

  const handleConfirm = useCallback(async () => {
    if (isAuthenticated || !signUpId?.trim()) {
      return;
    }
    const fullPhoneNumber = toCallablePhone(countryCode, nationalNumber);
    if (!fullPhoneNumber || fullPhoneNumber.length < 8) {
      posthog.capture(PATIENT_TRACKING_EVENTS.web_phone_verify_failed, { reason: 'numberUnknown' });
      setFieldError('Leider stimmt deine Handynummer nicht mit unseren Daten überein. Versuche es bitte erneut.');
      return;
    }

    setSubmitting(true);
    setFieldError(undefined);

    try {
      const result = await webAppOnboardingAuthentication({
        signedUpPatientId: signUpId,
        phone: fullPhoneNumber,
      });
      await signInWithCustomToken(auth, result.token);
      posthog.capture(PATIENT_TRACKING_EVENTS.web_phone_verify_completed, { authenticated: true });
      posthog.identify(signUpId, buildOnboardingPersonProperties(signUpId));
      setVerifiedPatientNamesFromAuth({
        firstName: result.patientFirstName,
        lastName: result.patientLastName,
      });
      setFieldError(undefined);
      permissionDeniedStreakRef.current = 0;
      setPermissionDeniedStreak(0);
      await navigateAfterAuth();
      return;
    } catch (error) {
      const isPermissionDenied = error instanceof FirebaseError && error.code === 'functions/permission-denied';
      posthog.capture(PATIENT_TRACKING_EVENTS.web_phone_verify_failed, { reason: 'numberUnknown' });

      if (isPermissionDenied) {
        permissionDeniedStreakRef.current += 1;
        const streak = permissionDeniedStreakRef.current;
        setPermissionDeniedStreak(streak);
        setFieldError(
          streak >= 2
            ? 'Leider stimmt deine Handynummer nicht mit unseren Daten überein. Versuche es bitte erneut oder kontaktiere uns.'
            : 'Leider stimmt deine Handynummer nicht mit unseren Daten überein. Versuche es bitte erneut.',
        );
        return;
      }

      setFieldError('Leider stimmt deine Handynummer nicht mit unseren Daten überein. Versuche es bitte erneut.');
    } finally {
      setSubmitting(false);
    }
  }, [isAuthenticated, countryCode, nationalNumber, signUpId, setVerifiedPatientNamesFromAuth, navigateAfterAuth, posthog]);

  const forwardDisabled = !authReady
    ? true
    : isAuthenticated
      ? postAuthNavigating
      : submitting || nationalNumber.replace(/\D/g, '').length < 6 || countryCode.replace(/\D/g, '').length < 1;

  const phoneInputDisabled = !authReady || submitting || isAuthenticated;
  const showRetrySupport = !isAuthenticated && permissionDeniedStreak >= 2;

  return (
    <OnboardingLayout
      currentStep={2}
      totalSteps={6}
      onBack={handleBack}
      onForward={isAuthenticated ? () => void navigateAfterAuth() : handleConfirm}
      forwardLabel={isAuthenticated ? 'Weiter' : 'Bestätigen'}
      forwardDisabled={forwardDisabled}
      forwardBusy={submitting || postAuthNavigating}
    >
      <OnboardingHeader />
      <OnboardingBody>
        <ContentStack>
          <H1>Bestätige deine Handynummer</H1>
          <Body variant="document">Zum Schutz deiner persönlichen Daten bestätige bitte deine Handynummer.</Body>
          {isAuthenticated ? (
            <>
              <PhoneNumberInput
                id="verify-phone"
                countryLabel="Land"
                nationalLabel="Telefonnummer*"
                countryCode={confirmedPhone?.countryCode ?? '+49'}
                nationalNumber={confirmedPhone?.nationalNumber ?? ''}
                setCountryCode={noop}
                setNationalNumber={noop}
                disabled={phoneInputDisabled}
              />
              <Notice
                preset="info"
                bodyTone="accent"
                icon={<SvgIcon name="check" size={24} primaryColor={colors.primaryDarkText} decorative />}
                aria-label="Handynummer bestätigt"
              >
                Du hast deine Handynummer bereits bestätigt und kannst mit dem nächsten Schritt weiter machen.
              </Notice>
            </>
          ) : (
            <>
              <PhoneNumberInput
                id="verify-phone"
                countryLabel="Land"
                nationalLabel="Telefonnummer*"
                countryCode={countryCode}
                nationalNumber={nationalNumber}
                setCountryCode={setCountryCode}
                setNationalNumber={setNationalNumber}
                phoneError={fieldError}
                disabled={phoneInputDisabled}
              />
              {showRetrySupport ? (
                <ErrorActions>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="large"
                    onClick={handleOpenSupport}
                    disabled={isOpeningSupportChat}
                    aria-busy={isOpeningSupportChat || undefined}
                  >
                    Support Kontaktieren
                  </Button>
                </ErrorActions>
              ) : null}
            </>
          )}
        </ContentStack>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  padding-bottom: ${space.xl};
`;

const ErrorActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${space.md};
`;

function noop() {}

function phonePartsFromSignedUpPatient(patient: SignedUpPatient): {
  countryCode: string;
  nationalNumber: string;
} {
  const cc = patient.patientPhoneCountryCode?.trim();
  const national = patient.patientPhoneNationalNumber?.trim();
  if (cc || national) {
    const countryCode = cc ? (cc.startsWith('+') ? cc : `+${cc.replace(/\D/g, '')}`) : '+49';
    return { countryCode, nationalNumber: national ?? '' };
  }
  if (patient.patientPhone?.trim()) {
    return parsePhoneForDisplay(patient.patientPhone);
  }
  return { countryCode: '+49', nationalNumber: '' };
}

function parsePhoneForDisplay(e164: string): { countryCode: string; nationalNumber: string } {
  const normalized = normalizePhoneForAuth(e164);
  const digits = normalized.replace(/\D/g, '');
  if (!digits) {
    return { countryCode: '+49', nationalNumber: '' };
  }
  if (digits.startsWith('49') && digits.length > 2) {
    return { countryCode: '+49', nationalNumber: digits.slice(2) };
  }
  if (digits.length > 3) {
    return { countryCode: `+${digits.slice(0, 2)}`, nationalNumber: digits.slice(2) };
  }
  return { countryCode: '+49', nationalNumber: digits };
}

function buildE164FromParts(countryCode: string, nationalNumber: string): string {
  const ccDigits = countryCode.replace(/\D/g, '');
  const nationalDigits = nationalNumber.replace(/\D/g, '');
  if (!ccDigits || !nationalDigits) {
    return '';
  }
  return `+${ccDigits}${nationalDigits}`;
}

function normalizePhoneForAuth(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('+')) {
    return `+${trimmed.slice(1).replace(/\D/g, '')}`;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) {
    return '';
  }

  if (digits.startsWith('00')) {
    return `+${digits.slice(2)}`;
  }
  if (digits.startsWith('0')) {
    return `+49${digits.slice(1)}`;
  }
  if (digits.startsWith('49')) {
    return `+${digits}`;
  }
  return `+${digits}`;
}

function toCallablePhone(countryCode: string, nationalNumber: string): string {
  return normalizePhoneForAuth(buildE164FromParts(countryCode, nationalNumber));
}
